<template>
  <nx-form :value="value"
           class="nx-scrollytelling-slide-editor-form"
           validation-mode="eager"
           v-on="$listeners"
           v-slot="{ draft, form }"
            ref="form">
    <nx-scrollytelling-slide-configuration-scope :draft="draft"/>
    <nx-form-scope-card :title="$t('message.scrollytelling.scopes.text')" icon="text_fields" class="mt-1" collapsable>
      <v-layout row wrap>
        <v-flex xs12 sm6>
          <nx-form-text
            name="title"
            :label="$t('message.scrollytelling.fields.title')"
            :value="draft.title"
          />
        </v-flex>
        <v-flex xs12 sm3 :class="{ 'pl-2 pr-2': $vuetify.breakpoint.smAndUp }">
          <nx-form-select
            name="titleSize"
            :label="$t('message.form.fields.size')"
            :items="textSizeItems"
            :value="draft.titleSize"
            default="medium"
          />
        </v-flex>
        <v-flex xs12 sm3 :class="{ 'pl-2': $vuetify.breakpoint.smAndUp }">
          <nx-form-select
            name="titleAlignment"
            :label="$t('message.form.fields.alignment')"
            :items="alignmentItems"
            :value="draft.titleAlignment"
            default=""
          />
        </v-flex>
        <v-flex xs12>
          <nx-form-content-editor
            class="mx-4"
            name="summary"
            type="scrollytelling"
            :label="$t('message.form.fields.summary')"
            :sidebar-options="sidebarOptions"
            :value="draft.summary"
          />
        </v-flex>
        <v-flex xs12 md9>
          <nx-form-text
            name="signatureAuthorName"
            :label="$t('message.form.fields.signatureAuthorName')"
            :value="draft.signatureAuthorName"
          />
        </v-flex>
        <v-flex xs12 md3 :class="{ 'pl-3': $vuetify.breakpoint.mdAndUp }">
          <nx-form-select
            name="signatureAlignment"
            :label="$t('message.form.fields.alignment')"
            :items="alignmentItems"
            :value="draft.signatureAlignment"
            default=""
          />
        </v-flex>
        <v-flex xs12 md6 :class="{ 'pr-3': $vuetify.breakpoint.mdAndUp }">
          <nx-form-select
            name="textPosition"
            :label="$t('message.form.fields.textHorizontalPosition')"
            :items="textHorizontalPositionItems"
            validation="required"
            :value="draft.textPosition"
          />
        </v-flex>
        <v-flex xs12 md6 :class="{ 'pl-3': $vuetify.breakpoint.mdAndUp }">
          <nx-form-select
            name="textVerticalPosition"
            :label="$t('message.form.fields.textVerticalPosition')"
            :items="textVerticalPositionItems"
            validation="required"
            :disabled="textVerticalPositionDisabled"
            :value="draft.textVerticalPosition"
          />
        </v-flex>
        <v-flex xs12 sm4 :class="{ 'pr-2': $vuetify.breakpoint.smAndUp }">
          <nx-form-color-picker
            name="textColor"
            :label="$t('message.form.fields.textColor')"
            default="000000"
            :value="draft.textColor"
          />
        </v-flex>
        <v-flex xs12 sm4 :class="{ 'pr-2 pl-2': $vuetify.breakpoint.smAndUp }">
          <nx-form-color-picker
            name="textBackgroundColor"
            :label="$t('message.scrollytelling.fields.textBackgroundColor')"
            :value="draft.textBackgroundColor"
          />
        </v-flex>
        <v-flex xs12 sm4 :class="{ 'pl-2': $vuetify.breakpoint.smAndUp }">
          <nx-form-select
            name="textBackgroundOpacityColor"
            :label="$t('message.scrollytelling.fields.textBackgroundOpacityColor')"
            :items="[100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0]"
            :default="100"
            :value="draft.textBackgroundOpacityColor"
          />
        </v-flex>
        <v-flex xs12>
          <nx-form-color-picker
            name="overlayColor"
            :label="$t('message.scrollytelling.fields.backgroundColor')"
            :value="draft.overlayColor"
            :hint="$t('message.scrollytelling.fields.backgroundColor')"
            persistent-hint
          />
        </v-flex>
        <v-flex xs12 class="mt-2">
          <nx-form-select
            name="textAnimation"
            :label="$t('message.scrollytelling.fields.animation')"
            :items="animationItems"
            :value="draft.textAnimation"
            default="none"
          />
        </v-flex>
      </v-layout>
    </nx-form-scope-card>
    <nx-scrollytelling-slide-background-scope :draft="draft" @selection="updateOnSelection">
      <v-flex xs12 md8>
        <nx-form-select
            name="backgroundPosition"
            :label="$t('message.form.fields.backgroundPosition')"
            :items="backgroundPositionItems"
            default="100w"
            :value="draft.backgroundPosition"
        />
      </v-flex>
      <v-flex xs12 md4 :class="{ 'pl-5': $vuetify.breakpoint.mdAndUp }">
        <nx-form-switch
            name="backgroundFixed"
            :label="$t('message.form.fields.backgroundFixed')"
            :value="draft.backgroundFixed"
        />
      </v-flex>
      <v-flex xs12 class="mt-2">
        <nx-form-select
          name="backgroundAnimation"
          :label="$t('message.scrollytelling.fields.animation')"
          :items="animationItems"
          :value="draft.backgroundAnimation"
          default="none"
        />
      </v-flex>
      <v-flex xs12 class="mt-2">
        <nx-form-select
          name="backgroundOpacityEffects"
          :label="$t('message.scrollytelling.fields.backgroundOpacityEffects')"
          :items="backgroundOpacityEffectsItems"
          :value="draft.backgroundOpacityEffects"
          default="none"
        />
      </v-flex>
    </nx-scrollytelling-slide-background-scope>
  </nx-form>
</template>

<script>
import NxFormScopeCard from '@/modules/form/components/FormScopeCard';
import NxForm from '@/modules/form/Form';
import NxFormText from '@/modules/form/components/FormText';
import NxFormSelect from '@/modules/form/components/FormSelect';
import NxFormColorPicker from '@/modules/form/components/FormColorPicker';
import NxScrollytellingSlideConfigurationScope
  from '@/modules/scrollytelling/components/form/components/ScrollytellingSlideConfigurationScope';
import NxScrollytellingSlideBackgroundScope
  from '@/modules/scrollytelling/components/form/components/ScrollytellingSlideBackgroundScope';
import NxFormSwitch from '@/modules/form/components/FormSwitch';
import NxFormContentEditor from '@/modules/form/components/FormContentEditor';


export default {
  name: 'nx-scrollytelling-slide-editor-form-opening',
  components: {
    NxFormSwitch,
    NxScrollytellingSlideBackgroundScope,
    NxScrollytellingSlideConfigurationScope,
    NxFormColorPicker,
    NxFormSelect,
    NxForm,
    NxFormText,
    NxFormScopeCard,
    NxFormContentEditor,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  computed: {
    sidebarOptions() {
      return [
        'ql-scrollytelling-spacer',
        'ql-scrollytelling-image',
        'ql-video',
        'ql-tweet',
        'ql-youtube',
        'ql-facebook',
        'ql-instagram',
        'ql-tiktok',
        'ql-code',
      ];
    },
    textVerticalPositionDisabled() {
      return this.$refs.form
        && this.$refs.form.draft
        && this.$refs.form.draft.backgroundPosition === '50h';
    },
    backgroundPositionItems() {
      return [
        {
          text: '100% Ancho',
          value: '100w',
        },
        {
          text: '50% Alto',
          value: '50h',
        },
        {
          text: 'Columna izquierda',
          value: 'left-column',
        },
        {
          text: 'Columna derecha',
          value: 'right-column',
        },
      ];
    },
    textHorizontalPositionItems() {
      return [
        {
          text: this.$t('message.common.left'),
          value: 'left',
        },
        {
          text: this.$t('message.common.center'),
          value: 'center',
        },
        {
          text: this.$t('message.common.right'),
          value: 'right',
        },
      ];
    },
    textVerticalPositionItems() {
      return [
        {
          text: this.$t('message.common.top'),
          value: 'top',
        },
        {
          text: this.$t('message.common.center'),
          value: 'center',
        },
        {
          text: this.$t('message.common.bottom'),
          value: 'bottom',
        },
      ];
    },
    textSizeItems() {
      return [
        {
          text: this.$t('message.common.large'),
          value: 'large',
        },
        {
          text: this.$t('message.common.medium'),
          value: 'medium',
        },
        {
          text: this.$t('message.common.small'),
          value: 'small',
        },
      ];
    },
    alignmentItems() {
      return [
        {
          text: this.$t('message.common.default'),
          value: '',
        },
        {
          text: this.$t('message.common.left'),
          value: 'left',
        },
        {
          text: this.$t('message.common.center'),
          value: 'center',
        },
        {
          text: this.$t('message.common.right'),
          value: 'right',
        },
      ];
    },
    animationItems() {
      return [
        {
          text: 'none',
          value: 'none',
        },
        {
          text: 'zoom-in',
          value: 'zoom-in',
        },
        {
          text: 'zoom-out',
          value: 'zoom-out',
        },
        {
          text: 'fade-out',
          value: 'fade-out',
        },
        {
          text: 'blur-out',
          value: 'blur-out',
        },
        {
          text: 'grayscale',
          value: 'grayscale',
        },
      ];
    },
    backgroundOpacityEffectsItems() {
      return [
        {
          text: 'none',
          value: 'none',
        },
        {
          text: 'central',
          value: 'central',
        },
        {
          text: 'top',
          value: 'top',
        },
        {
          text: 'bottom',
          value: 'bottom',
        },
        {
          text: 'left',
          value: 'left',
        },
        {
          text: 'right',
          value: 'right',
        },
      ];
    },
  },
  methods: {
    updateOnSelection() {
      if (this.$refs.form.draft
        && this.$refs.form.draft.backgroundDesktopImage
      ) {
        this.$refs.form.updateField('backgroundImageAlt', this.$refs.form.draft.backgroundDesktopImage.name);
      }
      if (this.$refs.form.draft
        && this.$refs.form.draft.backgroundMobileImage
      ) {
        this.$refs.form.updateField('backgroundImageAlt', this.$refs.form.draft.backgroundMobileImage.name);
      }
    },
  },
};
</script>
